import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import store from '@/store'
import awsConnection from '@/views/habit/aws'
import { useLambda } from '@/views/habit/awsLambda'
import endpoints from '@/libs/endpoints'
import { oid } from '@/helpers/mongoHelpers'
import { LOGO_CORREO } from '@/libs/emailAssets'

export default function useCommonTodo() {

  const userData = store.state?.userStore?.userData;
  const userId = userData.worker_id != null ? userData.worker_id.$oid : null
  const { commitment_functionality: commitmentFunctionality, default_language } = JSON.parse(localStorage.getItem("clientData") || '{}')

  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { singleUpload } = awsConnection()
  const { createItem, updateItem, updateItems, getItem, getItems, getItemsAggregate, sendEmail, getPresignedUploadUrl } = useLambda()

  const tagColors = {
    people: "primary",
    place: "info",
    processes: "warning",
    customer_need: "danger",
    tools_team: "info",
    positive_client_voice: "primary",
    negative_client_voice: "danger",
    safety: "primary",
    processes_productivity: "info",
    assets_management: "warning",
    quality: "danger",
    sustainability: "secondary",
    costs: "dark",
    rdp: "primary",
  }

  const resolveTagVariant = tag => {
    return tagColors[tag] || "primary"
  }
  
  const resolveAvatarVariant = tags => {
    if (!tags || !tags.length) return 'secondary'
    for (const tag of tags) {
      if (tagColors.hasOwnProperty(tag)) return tagColors[tag]
    }
    return 'primary'
  }

  const updateImprovementWithKey = async (improvementId, key) => {
   try {
      const query = { _id: oid(improvementId) }
      const action = { $set: { imageKey: key }, $push: { allFileKeys: { $each: key } } }

      await updateItem({ collection: 'improvement', query, action })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.improvement_update_error'))
    }
  }

  const getEmailTemplate = ({ name, title, description, stage, textMessage, origin, originId }) => {
    return `
      <html>
        <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
          <tr>
            <td style="background-color: #ecf0f1; text-align: center; padding: 0">
              <img width="20%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="AddCo Logo">
            </td>
          </tr>

          <tr>
            <td style="background-color: #ecf0f1">
              <div style="color: #34495e; margin: 2% 10%; font-family: sans-serif">
                <h2 style="color: #e67e22; margin: 0 0 7px">
                  ${i18n.t('message.hello')}${name ? ` ${name}` : ''},
                </h2>

                <p style="margin: 2px; font-size: 15px">
                  ${textMessage}:
                </p>

                <ul style="font-size: 15px;  margin: 10px 0">
                  ${title ? `<li><u>${i18n.t('label.Title')}</u>: <b>${title}</b></li>` : ''}
                  ${description ? `<li><u>${i18n.t('label.description')}</u>: <b>${description}</b></li>` : ''}
                  ${stage ? `<li><u>${i18n.t('label.stage')}</u>: <b>${i18n.t(`${origin}Stages.${stage}`)}</b></li>` : ''}
                </ul>

                <div style="width: 100%; text-align: center; margin: 7% 0%;">
                  <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #498ceb; font-size: 17px" href="${endpoints.app_url}/organization/${origin}/edit/${originId}">
                    ${origin === 'complaint' ? i18n.t('message.see_complaint_in_habit') : i18n.t('message.see_improvement_in_habit')}
                  </a>
                </div>
              </div>
            </td>
          </tr>
        </table>
      </html>
    `
  }

  const getPresenceValidationEmailTemplate = ({ name, process, supervisor, date, textMessage, presenceValidationId }) => {
    return `
      <html>
        <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
          <tr>
            <td style="background-color: #ecf0f1; text-align: center; padding: 0">
              <img width="20%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="AddCo Logo">
            </td>
          </tr>

          <tr>
            <td style="background-color: #ecf0f1">
              <div style="color: #34495e; margin: 2% 10%; font-family: sans-serif">
                <h2 style="color: #e67e22; margin: 0 0 7px">
                  ${i18n.t('message.hello')}${name ? ` ${name}` : ''},
                </h2>

                <p style="margin: 2px; font-size: 15px">
                  ${textMessage}:
                </p>

                <ul style="font-size: 15px;  margin: 10px 0">
                  ${process ? `<li><u>${i18n.t('message.tableHeader.process')}</u>: <b>${process}</b></li>` : ''}
                  ${supervisor ? `<li><u>${i18n.t('label.evaluator')}</u>: <b>${supervisor}</b></li>` : ''}
                  ${name ? `<li><u>${i18n.t('label.evaluated')}</u>: <b>${name}</b></li>` : ''}
                  ${date ? `<li><u>${i18n.t('message.tableHeader.date')}</u>: <b>${date}</b></li>` : ''}
                </ul>

                <div style="width: 100%; text-align: center; margin: 7% 0%;">
                  <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #498ceb; font-size: 17px" href="${endpoints.app_url}/presence-validation/${presenceValidationId}">
                    ${i18n.t('message.validate_presence')}
                  </a>
                </div>
              </div>
            </td>
          </tr>
        </table>
      </html>
    `
  }

  const getControlResponsibleEmailTemplate = ({ name, process, processCode, inherentRisk, residualRisk, control, population, sample, textMessage, confirmationId, controlIndex }) => {
    return `
      <html>
        <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
          <tr>
            <td style="background-color: #ecf0f1; text-align: center; padding: 0">
              <img width="20%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="AddCo Logo">
            </td>
          </tr>

          <tr>
            <td style="background-color: #ecf0f1">
              <div style="color: #34495e; margin: 2% 10%; font-family: sans-serif">
                <h2 style="color: #e67e22; margin: 0 0 7px">
                  ${i18n.t('message.hello')}${name ? ` ${name}` : ''},
                </h2>

                <p style="margin: 2px; font-size: 15px">
                  ${textMessage}:
                </p>

                <ul style="font-size: 15px;  margin: 10px 0">
                  ${process ? `<li><u>${i18n.t('message.tableHeader.process')}</u>: <b>${process}</b></li>` : ''}
                  ${processCode ? `<li><u>${i18n.t('label.code')}</u>: <b>${processCode}</b></li>` : ''}
                  ${control ? `<li><u>${i18n.t('label.control')}</u>: <b>${controlIndex != null ? `${controlIndex + 1}. ` : ''}${control}</b></li>` : ''}
                  ${inherentRisk ? `<li><u>${i18n.t('message.tableHeader.inherentRisk')}</u>: <b>${i18n.t(`residualRisk.${inherentRisk}`)}</b></li>` : ''}
                  ${residualRisk ? `<li><u>${i18n.t('message.tableHeader.residualRisk')}</u>: <b>${i18n.t(`residualRisk.${residualRisk}`)}</b></li>` : ''}
                  ${population ? `<li><u>${i18n.t('label.populationSize')}</u>: <b>${population}</b></li>` : ''}
                  ${sample ? `<li><u>${i18n.t('label.sampleNumberToRequest')}</u>: <b>${sample}</b></li>` : ''}
                </ul>

                <div style="width: 100%; text-align: center; margin: 7% 0%;">
                  <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #498ceb; font-size: 17px" href="${endpoints.app_url}/habit/confirmation/respond/${confirmationId}/${controlIndex}">
                    ${i18n.t('message.answer_control_evaluation')}
                  </a>
                </div>
              </div>
            </td>
          </tr>
        </table>
      </html>
    `
  }

  const getScheduledEvaluationEmailTemplate = ({ name, process, processCode, date, textMessage, processId, attendeeId, eventId, control, controlIndex, showStartEvaluationButton }) => {
    return `
      <html>
        <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
          <tr>
            <td style="background-color: #ecf0f1; text-align: center; padding: 0">
              <img width="20%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="AddCo Logo">
            </td>
          </tr>

          <tr>
            <td style="background-color: #ecf0f1">
              <div style="color: #34495e; margin: 2% 10%; font-family: sans-serif">
                <h2 style="color: #e67e22; margin: 0 0 7px">
                  ${i18n.t('message.hello')}${name ? ` ${name}` : ''},
                </h2>

                <p style="margin: 2px; font-size: 15px">
                  ${textMessage}:
                </p>

                <ul style="font-size: 15px;  margin: 10px 0">
                  ${process ? `<li><u>${i18n.t('message.tableHeader.process')}</u>: <b>${process}</b></li>` : ''}
                  ${control ? `<li><u>${i18n.t('label.control')}</u>: <b>${controlIndex != null ? `${controlIndex + 1}. ` : ''}${control}</b></li>` : ''}
                  ${date ? `<li><u>${i18n.t('message.tableHeader.date')}</u>: <b>${date}</b></li>` : ''}
                </ul>

                ${showStartEvaluationButton ? `
                <div style="width: 100%; text-align: center; margin: 7% 0%;">
                  <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #498ceb; font-size: 17px" href="${endpoints.app_url}/habit/confirmation/new/${processId}/${attendeeId}/${eventId}">
                    ${i18n.t('message.start_evaluation')}
                  </a>
                </div>
                ` : ''}
              </div>
            </td>
          </tr>
        </table>
      </html>
    `
  }

  const sendScheduledEvaluationEmail = async ({ evaluatorId, attendeeId, processInfo, eventDate, eventId, controls }) => {
    try {
      const evaluatorQuery = { _id: oid(evaluatorId) }
      const attendeeQuery = { _id: oid(attendeeId) }

      const [evaluatorData, attendeeData] = await Promise.all([
        getItem({ collection: 'worker', query: evaluatorQuery }),
        getItem({ collection: 'worker', query: attendeeQuery })
      ])

      const subject = i18n.t('message.evaluation_scheduled')
      const dateFormatted = eventDate ? new Date(eventDate).toLocaleDateString(`${default_language || 'en'}-US`) : ''

      const emailPromises = []

      // Enviar email al evaluador
      if (evaluatorData?.email) {
        const textMessage = i18n.t('message.evaluation_scheduled_evaluator_text')
        const body = getScheduledEvaluationEmailTemplate({
          name: evaluatorData.name,
          process: processInfo?.name,
          processCode: processInfo?.code,
          date: dateFormatted,
          textMessage,
          processId: processInfo?._id,
          attendeeId: attendeeId,
          eventId: eventId.toString(),
          showStartEvaluationButton: true,
        })

        emailPromises.push(
          sendEmail({ to: [evaluatorData.realEmail || evaluatorData.email], subject, htmlBody: body })
            .then(() => ({ recipient: 'evaluator', sent: true, name: evaluatorData.name }))
            .catch(() => ({ recipient: 'evaluator', sent: false, name: evaluatorData.name }))
        )
      }

      // Enviar email al evaluado
      if (attendeeData?.email) {
        const textMessage = i18n.t('message.evaluation_scheduled_attendee_text')
        const body = getScheduledEvaluationEmailTemplate({
          name: attendeeData.name,
          process: processInfo?.name,
          processCode: processInfo?.code,
          date: dateFormatted,
          textMessage,
          processId: processInfo?._id,
          attendeeId: attendeeId,
          eventId: eventId.toString(),
        })

        emailPromises.push(
          sendEmail({ to: [attendeeData.realEmail || attendeeData.email], subject, htmlBody: body })
            .then(() => ({ recipient: 'attendee', sent: true, name: attendeeData.name }))
            .catch(() => ({ recipient: 'attendee', sent: false, name: attendeeData.name }))
        )
      }

      // Enviar emails a los responsables de controles
      if (controls?.length) {
        // Obtener IDs únicos de responsables (evitar duplicados)
        const uniqueResponsibleIds = [...new Set(
          controls
            .filter(control => control.responsible_id)
            .map(control => control.responsible_id)
        )]

        // Obtener datos de todos los responsables en paralelo
        const responsiblesData = await Promise.all(
          uniqueResponsibleIds.map(responsibleId =>
            getItem({ collection: 'worker', query: { _id: oid(responsibleId) } })
          )
        )

        // Crear un mapa de responsableId -> datos del responsable
        const responsiblesMap = {}
        responsiblesData.forEach((data, idx) => {
          if (data) responsiblesMap[uniqueResponsibleIds[idx]] = data
        })

        // Enviar email a cada responsable con la lista de sus controles
        controls.forEach(control => {
          if (!control.responsible_id || control.skipNotification) return

          const responsibleData = responsiblesMap[control.responsible_id]
          if (!responsibleData?.email) return

          // No enviar duplicados si el responsable ya recibió un email como evaluador o evaluado
          if (responsibleData._id.toString() === evaluatorId ||
              responsibleData._id.toString() === attendeeId) {
            return
          }

          const textMessage = i18n.t('message.evaluation_scheduled_control_responsible_text')
          const body = getScheduledEvaluationEmailTemplate({
            name: responsibleData.name,
            process: processInfo?.name,
            processCode: processInfo?.code,
            control: control.name,
            controlIndex: control.index,
            date: dateFormatted,
            textMessage,
            processId: processInfo?._id,
            attendeeId: attendeeId,
            eventId: eventId.toString(),
          })

          emailPromises.push(
            sendEmail({ to: [responsibleData.realEmail || responsibleData.email], subject, htmlBody: body })
              .then(() => ({ recipient: 'control_responsible', sent: true, name: responsibleData.name, control: control.name }))
              .catch(() => ({ recipient: 'control_responsible', sent: false, name: responsibleData.name, control: control.name }))
          )
        })
      }

      const results = await Promise.all(emailPromises)
      const sentCount = results.filter(r => r.sent).length

      if (sentCount > 0) {
        showSuccessMessage(i18n.t('message.evaluation_scheduled_emails_sent', { count: sentCount }))
      }

      return results
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.evaluation_scheduled_emails_error'))
      return []
    }
  }

  const sendControlResponsibleEmail = async ({ responsibleWorkerId, controlName, controlIndex, population, sample, processInfo, confirmationId }) => {
    try {
      const query = { _id: responsibleWorkerId }
      const recipientData = await getItem({ collection: 'worker', query })

      if (!recipientData?.email) {
        return { responsible: recipientData?.name || '', email: null, control: controlName, sent: false, sendFailed: false }
      }

      const subject = i18n.t('message.control_responsible_evaluation_assigned')
      const textMessage = i18n.t('message.control_responsible_evaluation_assigned_text')
      const recipientEmail = recipientData.realEmail || recipientData.email

      const body = getControlResponsibleEmailTemplate({
        name: recipientData.name,
        process: processInfo?.name,
        processCode: processInfo?.code,
        inherentRisk: processInfo?.inherentRisk,
        residualRisk: processInfo?.residualRisk,
        control: controlName,
        population,
        sample,
        textMessage,
        confirmationId,
        controlIndex,
      })

      // invokeLambda no lanza excepción ante un error HTTP (ver awsLambda.js): en un error del
      // lado de la Lambda, sendEmail resuelve igual sin "MessageId" en vez de rechazar la promesa.
      // Hay que chequear el resultado explícitamente para no reportar como enviado un mail que
      // en realidad falló silenciosamente del lado del servidor.
      const emailResult = await sendEmail({ to: [recipientEmail], subject, htmlBody: body })

      if (!emailResult?.MessageId) {
        return { responsible: recipientData.name, email: recipientEmail, control: controlName, sent: false, sendFailed: true }
      }

      return { responsible: recipientData.name, email: recipientEmail, control: controlName, sent: true, sendFailed: false }
    } catch (error) {
      console.log(error)
      return { responsible: null, email: null, control: controlName, sent: false, sendFailed: true }
    }
  }

  const sendImprovementEmail = (improvement) => {
    const subject = i18n.t('message.improvement_opportunity_assigned')
    const bodyData = {
      name: improvement.assignee?.name,
      title: improvement.note,
      tags: improvement.tags?.map(e => i18n.t(`domain.${e}`)).join(" / "),
      creator: userData.fullName,
      dueDate: `${improvement.dueDate.slice(8, 10)}/${improvement.dueDate.slice(5, 7)}/${improvement.dueDate.slice(0, 4)}`,
      description: improvement.description?.replace(/<[^>]*>/g, ''),
    }
    if (improvement.metadata?.length) {
      improvement.metadata.forEach(e => {
        if (e.name === "creation_date") e.answer = `${e.answer.slice(8, 10)}/${e.answer.slice(5, 7)}/${e.answer.slice(0, 4)}`
        Object.assign(bodyData, {[e.name]: e.answer})
      })
    }
    const body = getEmailTemplate(bodyData)

    sendEmail({ to: [improvement.assignee.realEmail || improvement.assignee.email], subject, htmlBody: body })
      .then((response) => {
        if (response.MessageId) showSuccessMessage(i18n.t('message.email_send_improvement_success'))
      })
      .catch((err) => {
        console.log(err)
        showErrorMessage(i18n.t('message.email_send_improvement_error'))
      })
  }

  const addTask = val => {
    // return new Promise((resolve, reject) => {
    //   store.dispatch('app-todo/addTask', {...val})
    //     .then((response) => {
    //       if (response.data.errors) throw new Error()
    //       showSuccessMessage(commitmentFunctionality ? i18n.t('message.commitment_created') : i18n.t('message.improvement_created'))

    //       // Upload documents to AWS and then update the improvement in MongoDB with the AWS document key
    //       if (val.documentFile?.name) {
    //         const improvementId = response.data.data.insertOneImprovement._id
    //         uploadFile(val.documentFile, "documents", improvementId)
    //       }
  
    //       // Send email to assignee with the details of the improvement opportunity created
    //       if (val.assignee?.email) sendImprovementEmail(val)

    //       resolve()
    //     })
    //     .catch((error) => {
    //       showErrorMessage(commitmentFunctionality ? i18n.t('message.commitment_error') : i18n.t('message.improvement_error'))
    //       console.log(error)
    //       reject(error)
    //     })
    // })
  }

  const uploadFileToS3 = async (file, destinationFolder) => {
    try {
      const { uploadUrl, key } = await getPresignedUploadUrl({ fileName: file.name, fileType: file.type, destinationFolder })

      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      })

      return key
    } catch (error) {
      console.log(error)
      throw new Error(error)
    }
  }

  const uploadImprovementFile = async (files, folder, improvementId) => {
    const destinationFolder = `${userData.client.$oid}/${folder}`

    const uploadFilesPromises = files?.map(file => {
      if (!file?.name) return null
      return new Promise((resolve, reject) => {
        uploadFileToS3(file, destinationFolder)
          .then((key) => resolve(key))
          .catch((err) => {
            console.log(err)
            resolve(null)
          })
      })
    }) || []

    const fileKeys = await Promise.all(uploadFilesPromises)

    updateImprovementWithKey(improvementId, fileKeys.filter(e => !!e))
  }

  const uploadAction_planFile = (file, folder) => {
    const destinationFolder = `${userData.client.$oid}/${folder}`
    return singleUpload(file, destinationFolder)
      .then((key) => {return key})
      .catch((err) => console.log(err))
  }

  const sendEmailImprovements = async (improvements, state, improvementsToUpdate, improvementsToDelete) => {
    const emailPromises = []
    for (const i of improvements) {  
      // If improvement is not new or flagged for update/delete or assigne doesn/t have email then continue (don't send email)
      if (!state && i._id && !improvementsToUpdate?.find(e => e._id === i._id) && !improvementsToDelete?.find(e => e._id === i._id)) {
        continue
      }

      let recipients = []

      if (i.assignee?.email) recipients.push({ name: i.assignee.name, email: i.assignee.email, category: "assignee" })

      if (i.subscribers?.length) {
        try {
          const subscribersData = i.subscribers[0]?._id ? i.subscribers : await getWorkersEmails(i.subscribers)
          recipients = [...recipients, ...subscribersData]
        } catch (error) {
          console.log(error)
        }
      }

      if (!recipients.length) continue

      const updatedState = state
          ? state
          : !i._id
            ? "add"
            : improvementsToDelete?.find(e => e._id === i._id)
              ? "delete"
              : "update"
  
        let subject
        switch (updatedState) {
          case "update":
            subject = commitmentFunctionality ? i18n.t('message.commitment_updated') : i18n.t('message.improvement_opportunity_updated')
            break;
          case "complete":
            subject = commitmentFunctionality ? i18n.t('message.commitment_completed') : i18n.t('message.improvement_opportunity_completed')
            break;
          case "delete":
            subject = commitmentFunctionality ? i18n.t('message.commitment_deleted') : i18n.t('message.improvement_opportunity_deleted')
            break;
          default:
            subject = commitmentFunctionality ? i18n.t('message.commitment_assigned') : i18n.t('message.improvement_opportunity_assigned')
            break;
        }
        const bodyData = {
          title: i.note,
          assignee: i.assignee?.name,
          tags: i.tags?.map(e => i18n.t(`domain.${e}`)).join(" / "),
          creator: userData.username,
          dueDate: i.dueDate instanceof Date?i.dueDate.toLocaleString():`${i.dueDate?.slice(8, 10)}/${i.dueDate?.slice(5, 7)}/${i.dueDate?.slice(0, 4)}`,
          description: i.description?.replace(/<[^>]*>/g, ''),
          commitmentFunctionality,
          updatedState,
          completed: i.completed,
          deleted: i.deleted,
        }
        if (i.metadata?.length) {
          i.metadata.forEach(e => {
            if (e.name === "creation_date") e.answer = `${e.answer.slice(8, 10)}/${e.answer.slice(5, 7)}/${e.answer.slice(0, 4)}`
            Object.assign(bodyData, {[e.name]: e.answer})
          })
        }

      recipients.forEach(r => {
        bodyData.name = r.name
        bodyData.category = r.category

        const body = getEmailTemplate(bodyData)
  
        const emailPromise = new Promise((resolve, reject) => {
          sendEmail({ to: [r.realEmail || r.email], subject, htmlBody: body })
            .then(res => {
              res.MessageId && resolve(res.MessageId)
            })
            .catch(err => console.log(err))
        })
  
        emailPromises.push(emailPromise)
      })
    }

    return Promise.all(emailPromises)
      .then(emailResponses => {
        const cantResponses = emailResponses.filter(res => !!res)
        if (cantResponses.length > 0) showSuccessMessage(`${cantResponses.length} ${commitmentFunctionality ? i18n.t('message.email_send_commitment_success') : i18n.t('message.email_send_improvement_success')}`) 
      })
      .catch((err) => {
        console.log(err)
        showErrorMessage(commitmentFunctionality ? i18n.t('message.email_send_commitment_error') : i18n.t('message.email_send_improvement_error'))
      })
  }

  const sendEmailCase = async (originData, state, recipient, origin, complaintRecipients = {}) => {
    try {
      let recipientData = []

      // For new complaints, send data to the company investigators
      if (state === 'add' && origin === 'complaint') {
        if (!originData.companyId) return

        // Get company data with investigators
        const companyQuery = { _id: oid(originData.companyId) }
        const company = await getItem({ collection: 'company', query: companyQuery })
        if (!company) return

        // Get investigators based on complaint type
        const investigatorFieldPrefix = originData.type === 'economicCrimesLaw' ? 'economicCrimesLaw'
          : originData.type === 'karinLaw' ? 'karinLaw'
          : 'ethicalSituations'

        const mainInvestigators = company[`${investigatorFieldPrefix}MainInvestigators`] || []
        const secondaryInvestigators = company[`${investigatorFieldPrefix}SecondaryInvestigators`] || []

        if (!mainInvestigators.length && !secondaryInvestigators.length) return

        // Get all investigators data
        const allInvestigatorIds = [...mainInvestigators, ...secondaryInvestigators]
        const investigatorsQuery = { _id: { $in: allInvestigatorIds.map(id => oid(id)) } }
        const allInvestigators = await getItems({ collection: 'worker', query: investigatorsQuery })
        if (!allInvestigators?.length) return

        // Helper function to check if a worker is the reported person
        const isReportedPerson = (worker) => {
          if (originData.type === 'karinLaw') {
            return (worker.email && worker.email === originData.reportedPersonData?.email)
              || (worker.realEmail && worker.realEmail === originData.reportedPersonData?.email)
              || worker.name === originData.reportedPersonData?.name
          } else {
            // economicCrimesLaw or ethicalSituations
            return originData.reportedPerson?.some(rp => worker._id?.toString() === rp.value.toString())
          }
        }

        // Get main and secondary investigators with their data
        const mainInvestigatorsData = allInvestigators.filter(w =>
          mainInvestigators.some(id => id.toString() === w._id.toString())
        )
        const secondaryInvestigatorsData = allInvestigators.filter(w =>
          secondaryInvestigators.some(id => id.toString() === w._id.toString())
        )

        // Check if any main investigator is the reported person
        const isMainInvestigatorReported = mainInvestigatorsData.some(w => isReportedPerson(w))

        // Determine recipients based on the logic
        if (isMainInvestigatorReported) {
          // If a main investigator is reported, send to other main investigators + all secondary
          const otherMainInvestigators = mainInvestigatorsData.filter(w => !isReportedPerson(w))
          recipientData = [...otherMainInvestigators, ...secondaryInvestigatorsData]
        } else {
          // If no main investigator is reported, send only to main investigators
          recipientData = mainInvestigatorsData
        }

        // Determine proposal responsible: first main investigator who is not reported
        let proposalResponsibleId = null
        const availableMainInvestigator = mainInvestigatorsData.find(w => !isReportedPerson(w))

        if (availableMainInvestigator) {
          proposalResponsibleId = availableMainInvestigator._id
        } else if (secondaryInvestigatorsData.length > 0) {
          // If all main investigators are reported, use first secondary investigator
          proposalResponsibleId = secondaryInvestigatorsData[0]._id
        }

        // Update complaint with the proposal responsible
        if (proposalResponsibleId) {
          try {
            const query = { _id: oid(originData._id) }
            const action = { $set: { proposalResponsible: oid(proposalResponsibleId) } }
            await updateItem({ collection: 'complaint', query, action })
          } catch (error) {
            console.log(error)
          }
        }
      } else {
        const query = { _id: Array.isArray(originData[recipient]) ? { $in: originData[recipient].map(e => oid(e)) } : oid(originData[recipient]) }
        const items = await getItems({ collection: 'worker', query })
        recipientData = items || []
      }

      if (!recipientData?.length) return

      const messages = {
        add: {
          subject: origin === 'complaint' ? i18n.t('message.complaint_created') : i18n.t('message.improvement_opportunity_created'),
          text: origin === 'complaint' ? i18n.t('message.following_complaint_created') : i18n.t('message.assigned_responsible_improvement'),
        },
        update: {
          subject: origin === 'complaint' ? i18n.t('message.complaint_updated') : i18n.t('message.improvement_opportunity_updated'),
          text: origin === 'complaint' ? i18n.t('message.complaint_assigned_updated') : i18n.t('message.improvement_assigned_updated'),
        },
        complete: {
          subject: origin === 'complaint' ? i18n.t('message.complaint_completed') : i18n.t('message.improvement_opportunity_completed'),
          text: origin === 'complaint' ? i18n.t('message.complaint_assigned_completed') : i18n.t('message.improvement_assigned_completed'),
        },
        delete: {
          subject: origin === 'complaint' ? i18n.t('message.complaint_deleted') : i18n.t('message.improvement_opportunity_deleted'),
          text: origin === 'complaint' ? i18n.t('message.complaint_assigned_deleted') : i18n.t('message.improvement_assigned_deleted'),
        },
      }

      for (const r of recipientData) {
        if (!r.email) continue

        const bodyData = {
          name: r.name,
          title: originData.note,
          description: originData.description?.length > 300 ? originData.description.slice(0, 300) + "..." : originData.description,
          stage: originData.stage,
          textMessage: messages[state].text,
          origin,
          originId: originData._id,
        }
        const body = getEmailTemplate(bodyData)
  
        sendEmail({ to: [r.realEmail || r.email], subject: messages[state].subject, htmlBody: body })
          .then(res => {
            showSuccessMessage(origin === 'complaint' ? i18n.t('message.email_send_complaint_success') : i18n.t('message.email_send_improvement_success'))
          })
          .catch(err => {
            console.log(err)
            showErrorMessage(origin === 'complaint' ? i18n.t('message.email_send_complaint_error') : i18n.t('message.email_send_improvement_error'))
          })
      }
    } catch (error) {
      console.log(error)
    }
  }

  const sendEmailActionPlan = async (actionPlanData, state, recipient, origin) => {
    try {
      const query = { _id: actionPlanData[recipient] }
      const recipientData = await getItem({ collection: 'worker', query })

      if (!recipientData.email) return

      const messages = {
        add: {
          subject: i18n.t('message.action_plan_created'),
          text: i18n.t('message.assigned_responsible_action_plan'),
        },
      }

      const bodyData = {
        name: recipientData.name,
        title: actionPlanData.name,
        description: actionPlanData.description,
        textMessage: messages[state].text,
        origin,
        originId: actionPlanData.originId,
      }

      const body = getEmailTemplate(bodyData)

      sendEmail({ to: [recipientData.realEmail || recipientData.email], subject: messages[state].subject, htmlBody: body })
        .then(res => {
          showSuccessMessage(i18n.t('message.email_send_action_plan_success'))
        })
        .catch(err => {
          console.log(err)
          showErrorMessage(i18n.t('message.email_send_action_plan_error'))
        })
    } catch (error) {
      console.log(error)
    }
  }

  const sendPresenceValidationEmail = async (evaluationData) => {
    try {
      const workerQuery = { _id: evaluationData.worker }
      const recipientData = await getItem({ collection: 'worker', query: workerQuery })

      if (!recipientData.email) {
        showErrorMessage(i18n.t('message.evaluated_without_email'))
        throw new Error("Worker without email")
      }

      let supervisorData
      if (evaluationData.supervisor) {
        const supervisorQuery = { _id: evaluationData.supervisor }
        supervisorData = await getItem({ collection: 'worker', query: supervisorQuery })
      }

      const subject = i18n.t('message.validate_evaluation_presence')
      const text = i18n.t('message.press_button_to_validate_presence')
      const evaluationDate = evaluationData.date ? new Date(evaluationData.date) : null

      const bodyData = {
        name: recipientData.name,
        textMessage: text,
        process: evaluationData.process,
        supervisor: supervisorData?.name,
        date: evaluationDate ? evaluationDate.toLocaleDateString(`${default_language || 'en'}-US`) : '',
        presenceValidationId: evaluationData.presenceValidationId,
      }

      const body = getPresenceValidationEmailTemplate(bodyData)

      return sendEmail({ to: [recipientData.realEmail || recipientData.email], subject, htmlBody: body })
        .then(res => {
          showSuccessMessage(i18n.t('message.presence_validation_email_sent'))
        })
        .catch(error => {
          throw new Error(error)
        })
    } catch (error) {
      throw new Error(error)
    }
  }

  const getWorkersEmails = async (workerIds) => {
    const query = { _id: { $in: workerIds.map(e => oid(e)) } }
    return await getItems({ collection: 'worker', query, options: { projection : { name: 1, email: 1 } } })
  }
    
  return {
    resolveTagVariant,
    resolveAvatarVariant,
    updateImprovementWithKey,
    getEmailTemplate,
    addTask,
    sendImprovementEmail,
    uploadImprovementFile,
    sendEmailImprovements,
    uploadAction_planFile,
    sendEmailCase,
    sendEmailActionPlan,
    sendPresenceValidationEmail,
    sendControlResponsibleEmail,
    sendScheduledEvaluationEmail,
    uploadFileToS3,
  }
}